'use client';

import Link from '@/components/NoPrefetchLink';
import { useEffect, useMemo, useState } from 'react';
import type { AwardWinnersResponse, AwardWinner } from '@/lib/types';
import { fetchAwardWinners } from '@/lib/apiClient';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import { ScrollToTopButton } from './ScrollToTopButton';
import '@/styles/InfoPage.css';

type Props = { locale: Locale; awardId: string; apiAwardId: string; description: string; initialData?: AwardWinnersResponse; focusYear?: number | null };
type MlbLeagueTab = { id: 'mlb-two-leagues' | 'mlb-other'; label: string; leagues: string[] };

const awardSlug = (awardId: string): string => awardId.replace(/ /g, '-');

const POSITION_ORDER = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH', 'UT'];

export default function AwardDetailClient({ locale, awardId: rawAwardId, apiAwardId, description, initialData, focusYear: focusYearProp = null }: Props) {
    const [data, setData] = useState<AwardWinnersResponse | null>(initialData ?? null);
    const [loading, setLoading] = useState(!initialData);
    const [notFound, setNotFound] = useState(false);
    const [activeLeagueTabId, setActiveLeagueTabId] = useState<MlbLeagueTab['id'] | null>(null);
    const [urlFocusYear, setUrlFocusYear] = useState<number | null>(null);
    const focusYear = focusYearProp ?? urlFocusYear;

    const t = (ja: string, en: string, es: string) =>
        locale === 'en' ? en : locale === 'es' ? es : ja;

    const playerName = (winner: AwardWinner) => {
        if (locale === 'ja') return winner.name;
        return winner.nameEn || winner.name;
    };

    const awardTitle = (res: AwardWinnersResponse) =>
        locale === 'en'
            ? (res.awardNameEn || res.awardId)
            : locale === 'es'
            ? (res.awardNameEs || res.awardNameEn || res.awardId)
            : (res.awardNameJa || res.awardId);

    const fallbackAwardTitle = useMemo(() => {
        if (!rawAwardId) return t('メジャーリーグ（MLB）アワード', 'Major League Baseball (MLB) Awards', 'Premios de Grandes Ligas (MLB)');
        try {
            return decodeURIComponent(rawAwardId).replace(/-/g, ' ');
        } catch {
            return rawAwardId.replace(/-/g, ' ');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawAwardId, locale]);

    useEffect(() => {
        if (initialData) {
            setData(initialData);
            setLoading(false);
            setNotFound(false);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setNotFound(false);
        fetchAwardWinners(apiAwardId, undefined, undefined, controller.signal)
            .then((res) => {
                if (!res) {
                    setNotFound(true);
                    return;
                }
                setData(res);
                setNotFound(false);
            })
            .catch((error: unknown) => {
                if (error instanceof Error && error.name === 'AbortError') return;
                setNotFound(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [apiAwardId, initialData]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setUrlFocusYear(parseFocusYear(params.get('year') ?? params.get('awardYear')));
    }, []);


    const positionOrder = (pos: string | null) => {
        const idx = POSITION_ORDER.indexOf(pos ?? '');
        return idx === -1 ? POSITION_ORDER.length : idx;
    };

    const translatePosition = (pos: string | null): string | null => {
        if (!pos) return null;
        if (locale === 'ja') {
            const map: Record<string, string> = {
                P: '投手', C: '捕手', '1B': '一塁手', '2B': '二塁手', '3B': '三塁手',
                SS: '遊撃手', LF: '左翼手', CF: '中堅手', RF: '右翼手',
                OF: '外野手', DH: '指名打者', UT: 'ユーティリティ',
            };
            return map[pos] ?? pos;
        }
        if (locale === 'es') {
            const map: Record<string, string> = {
                P: 'P', C: 'C', '1B': '1B', '2B': '2B', '3B': '3B',
                SS: 'SS', LF: 'JI', CF: 'JC', RF: 'JD',
                OF: 'JO', DH: 'BD', UT: 'Utilidad',
            };
            return map[pos] ?? pos;
        }
        if (pos === 'UT') return 'Utility';
        return pos;
    };

    const translateLeague = (lg: string) => {
        if (lg === 'AL') return t('ア・リーグ', 'AL', 'LA');
        if (lg === 'NL') return t('ナ・リーグ', 'NL', 'LN');
        if (lg === 'ML') return t('メジャーリーグ', 'Major League Baseball (MLB)', 'Grandes Ligas (MLB)');
        if (lg === 'AA') return t('アメリカン・アソシエーション', 'American Association', 'Asociación Americana');
        return lg;
    };

    const formatMonthName = (month: string): string => {
        const date = new Date(2000, Number(month) - 1, 1);
        return date.toLocaleString(locale === 'es' ? 'es-ES' : 'en-US', { month: 'short' });
    };

    const formatPeriodDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        if (locale === 'ja') return `${date.getMonth() + 1}/${date.getDate()}`;
        return date.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric' });
    };

    const formatPeriod = (winner: AwardWinner): string => {
        const label = winner.periodLabel;
        if (!label) return '-';
        const monthRange = label.match(/^(\d{4})-(\d{2})_(\d{2})$/);
        if (monthRange) {
            const [, , m1, m2] = monthRange;
            return locale === 'ja' ? `${Number(m1)}・${Number(m2)}月` : `${formatMonthName(m1)}/${formatMonthName(m2)}`;
        }
        const month = label.match(/^(\d{4})-(\d{2})$/);
        if (month) {
            const [, , m] = month;
            return locale === 'ja' ? `${Number(m)}月` : formatMonthName(m);
        }
        if (label.startsWith('Week of ')) {
            const d = label.replace('Week of ', '');
            if (locale === 'ja') return `${formatPeriodDate(d)}週`;
            if (locale === 'es') return `Semana del ${formatPeriodDate(d)}`;
            return `Week of ${formatPeriodDate(d)}`;
        }
        return label;
    };

    const normalizeLeague = (league: string | null | undefined): string => {
        const raw = (league ?? '').trim();
        const upper = raw.toUpperCase();
        if (upper === 'AL') return 'AL';
        if (upper === 'NL') return 'NL';
        if (upper === 'ML' || upper === 'MLB') return 'ML';
        return upper || raw;
    };

    const LEAGUE_ORDER = ['AL', 'NL', 'ML', 'AA'];
    const allLeagues = data ? Array.from(new Set(data.winners.map((w) => normalizeLeague(w.league)))) : [];
    const hasLeague = allLeagues.some((l) => l !== '');
    const leagues = hasLeague
        ? allLeagues.filter((l) => l !== '').sort((a, b) => {
            const ia = LEAGUE_ORDER.indexOf(a);
            const ib = LEAGUE_ORDER.indexOf(b);
            if (ia !== -1 && ib !== -1) return ia - ib;
            if (ia !== -1) return -1;
            if (ib !== -1) return 1;
            return a.localeCompare(b);
        })
        : [''];

    const leagueTabs = useMemo<MlbLeagueTab[]>(() => {
        if (!hasLeague) {
            return [{
                id: 'mlb-other',
                label: t('受賞者', 'Winner', 'Ganador'),
                leagues: [''],
            }];
        }

        const twoLeagues = leagues.filter((lg) => lg === 'AL' || lg === 'NL');
        const otherLeagues = leagues.filter((lg) => lg !== 'AL' && lg !== 'NL');
        const tabs: MlbLeagueTab[] = [];

        if (twoLeagues.length > 0) {
            tabs.push({
                id: 'mlb-two-leagues',
                label: t(
                    'ア・リーグ / ナ・リーグ',
                    'AL / NL',
                    'LA / LN',
                ),
                leagues: twoLeagues,
            });
        }
        if (otherLeagues.length > 0) {
            tabs.push({
                id: 'mlb-other',
                label: t(
                    'メジャーリーグ その他',
                    'Major League Baseball (MLB) and Others',
                    'Grandes Ligas (MLB) y Otros',
                ),
                leagues: otherLeagues,
            });
        }
        return tabs.length > 0 ? tabs : [{
            id: 'mlb-other',
            label: t('受賞者', 'Winner', 'Ganador'),
            leagues: [''],
        }];
    }, [hasLeague, leagues, locale]);

    useEffect(() => {
        if (activeLeagueTabId && !leagueTabs.some((tab) => tab.id === activeLeagueTabId)) {
            setActiveLeagueTabId(null);
        }
    }, [leagueTabs, activeLeagueTabId]);

    const preferredLeagueTabId = (() => {
        if (focusYear && data) {
            const targetTab = leagueTabs.find((tab) =>
                data.winners.some((winner) =>
                    winner.yearId === focusYear && tab.leagues.includes(normalizeLeague(winner.league)),
                ),
            );
            if (targetTab) return targetTab.id;
        }
        return leagueTabs[0]?.id ?? 'mlb-two-leagues';
    })();
    const activeLeagueTab = leagueTabs.find((tab) => tab.id === (activeLeagueTabId ?? preferredLeagueTabId)) ?? leagueTabs[0] ?? null;
    const visibleLeagues = activeLeagueTab?.leagues ?? [''];

    useEffect(() => {
        if (!focusYear || !data) return;
        const frame = window.requestAnimationFrame(() => {
            const target = document.getElementById(`award-year-${focusYear}`);
            if (!target) return;
            const headerEl = document.querySelector('.app-header') as HTMLElement | null;
            const headerHeight = headerEl?.getBoundingClientRect().height ?? 0;
            const awardHeaderEl = document.querySelector('.award-detail-sticky-header') as HTMLElement | null;
            const awardHeaderHeight = awardHeaderEl?.getBoundingClientRect().height ?? 0;
            const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - awardHeaderHeight - 12;
            window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [focusYear, data, activeLeagueTab?.id]);

    if (loading) {
        return <div className="info-page"><p>{t('読み込み中...', 'Loading...', 'Cargando...')}</p></div>;
    }

    if (notFound || !data) {
        return (
            <div className="info-page">
                <p>{t('アワードが見つかりませんでした。', 'Award not found.', 'Premio no encontrado.')}</p>
            </div>
        );
    }

    const formatLeagueDetail = (winner: AwardWinner): string => {
        const league = winner.league ? translateLeague(normalizeLeague(winner.league)) : '';
        const position = winner.position ? translatePosition(winner.position) : '';
        const label = [league, position].filter(Boolean).join(' / ');
        return label || '-';
    };

    const detailRows = data.winners
        .filter((winner) => visibleLeagues.some((league) => normalizeLeague(winner.league) === league))
        .sort((a, b) => {
            if (a.yearId !== b.yearId) return b.yearId - a.yearId;
            const periodCmp = (b.periodEnd || '').localeCompare(a.periodEnd || '');
            if (periodCmp !== 0) return periodCmp;
            const leagueCmp = normalizeLeague(a.league).localeCompare(normalizeLeague(b.league));
            if (leagueCmp !== 0) return leagueCmp;
            const positionCmp = positionOrder(a.position) - positionOrder(b.position);
            if (positionCmp !== 0) return positionCmp;
            return playerName(a).localeCompare(playerName(b));
        });
    const showPeriodColumn = detailRows.some((winner) =>
        Boolean(winner.periodLabel?.trim() || winner.periodStart?.trim() || winner.periodEnd?.trim()),
    );
    const showTeamColumn = detailRows.some((winner) => Boolean(winner.teamName?.trim()));
    const yearRowSpans = detailRows.reduce<Map<number, number>>((acc, winner) => {
        acc.set(winner.yearId, (acc.get(winner.yearId) ?? 0) + 1);
        return acc;
    }, new Map<number, number>());

    return (
        <div className="info-page">
            <div className="award-detail-sticky-header">
                <nav className="awards-breadcrumb">
                    <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                    {' › '}
                    <Link href={withLocalePrefix(locale, '/awards')}>
                        {t('メジャーリーグ（MLB）アワード一覧', 'Major League Baseball (MLB) Awards', 'Premios de Grandes Ligas (MLB)')}
                    </Link>
                    {' › '}
                    <span>{awardTitle(data)}</span>
                </nav>

                <h1>{awardTitle(data)}</h1>
                <p className="awards-description award-detail-caption">{description}</p>
            </div>

            {leagueTabs.length > 1 && (
                <div
                    className="info-tabs"
                    role="tablist"
                    aria-label={t(
                        'MLBリーグ表示切替',
                        'Switch MLB league groups',
                        'Cambiar grupos de liga MLB',
                    )}
                >
                    {leagueTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={activeLeagueTab?.id === tab.id}
                            className={`info-tab-button ${activeLeagueTab?.id === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveLeagueTabId(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="awards-table-wrapper">
                <table className="stats-table awards-stats-table">
                    <thead>
                        <tr>
                            <th>{t('年', 'Year', 'Año')}</th>
                            {showPeriodColumn && <th>{t('月/週', 'Period', 'Periodo')}</th>}
                            <th>{t('リーグ', 'League', 'Liga')}</th>
                            <th>{t('選手名', 'Player', 'Jugador')}</th>
                            {showTeamColumn && <th>{t('チーム名', 'Team', 'Equipo')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {detailRows.map((winner, index) => {
                            const showYear = index === 0 || detailRows[index - 1].yearId !== winner.yearId;
                            return (
                                <tr key={`${winner.yearId}-${winner.periodLabel || ''}-${winner.league || ''}-${winner.position || ''}-${winner.name}-${index}`} id={showYear ? `award-year-${winner.yearId}` : undefined}>
                                    {showYear && (
                                        <td className="numeric" rowSpan={yearRowSpans.get(winner.yearId) ?? 1}>{winner.yearId}</td>
                                    )}
                                    {showPeriodColumn && <td>{formatPeriod(winner)}</td>}
                                    <td>{formatLeagueDetail(winner)}</td>
                                    <td>
                                        {winner.slug ? (
                                            <Link href={`${withLocalePrefix(locale, `/players/${winner.slug}`)}?from=awards&awardSlug=${rawAwardId}&awardNameEn=${encodeURIComponent(data.awardNameEn || data.awardId)}&awardNameJa=${encodeURIComponent(data.awardNameJa || data.awardId)}&awardNameEs=${encodeURIComponent(data.awardNameEs || data.awardNameEn || data.awardId)}`}>
                                                {playerName(winner)}
                                            </Link>
                                        ) : playerName(winner)}
                                    </td>
                                    {showTeamColumn && <td>{winner.teamName || '-'}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <ScrollToTopButton />
        </div>
    );
}

const parseFocusYear = (value: string | null): number | null => {
    const year = value ? Number(value) : NaN;
    return Number.isInteger(year) && year > 0 ? year : null;
};
